import { queriesAccounts } from "@query";
import { useQueries } from "@tanstack/react-query";
import { Account } from "@wingspanhq/wingspan-api-sdk";
import uniq from "lodash/uniq";

/**
 * Resolve a set of payer accounts by id into an `accountsById` lookup, for the
 * employer-name column on the payee's paystub history.
 *
 * The pay-statement list is flat (each row carries only `payerAccountId`) and
 * `listAccounts` cannot filter by a set of ids, so each distinct payer is read
 * with `getAccount`. `useQueries` fans the reads out in parallel, keyed by the
 * same registry cache key as `queriesAccounts.getAccount.useQuery`, so they
 * dedupe with (and prime) that cache.
 */
export const useQueryPayerAccounts = (
  payerAccountIds: string[]
): { accountsById: Map<string, Account>; isLoading: boolean } => {
  const ids = uniq(payerAccountIds.filter(Boolean));

  const results = useQueries({
    queries: ids.map(accountId => ({
      queryKey: queriesAccounts.getAccount.getQueryKey({ accountId }),
      queryFn: () => queriesAccounts.getAccount.fetchQueryData({ accountId })
    }))
  });

  const accountsById = new Map<string, Account>();
  results.forEach((result, index) => {
    if (result.data) {
      accountsById.set(ids[index], result.data);
    }
  });

  return {
    accountsById,
    isLoading: results.some(result => result.isLoading)
  };
};
